/**
 * CRMBulkUpload
 * Super Admin — CRM > Bulk Upload
 *
 * Features:
 *  - Download sample CSV
 *  - Drag & drop / click CSV upload zone
 *  - Client-side CSV parse + preview (first 10 rows)
 *  - Client-side validation summary
 *  - Import to API with progress + result summary
 *  - Upload history table
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  ChevronDown,
  X,
  Eye,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com';

const authHeaders = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('session_token'),
  'Content-Type': 'application/json',
});

// ── Column name aliases ────────────────────────────────────────
// Maps any common header variation → canonical field name
const COLUMN_ALIASES = {
  // organization_name
  organization_name:    ['organization_name','org_name','college_name','college','university_name','university','company_name','company','institute_name','institution_name','school_name','name_of_institution','institution','name_of_college','name_of_company','name'],
  // mobile_number
  mobile_number:        ['mobile_number','mobile_no','mobile','phone_number','phone_no','phone','contact_number','contact_no','contact','mob','telephone','tel','number','mob_no'],
  // organization_type
  organization_type:    ['organization_type','org_type','type','category','institution_type','college_type'],
  // contact_person
  contact_person:       ['contact_person','contact_name','person_name','poc','point_of_contact','representative','rep','name_of_contact','incharge','in_charge'],
  // designation
  designation:          ['designation','role','position','title','job_title'],
  // alternate_number
  alternate_number:     ['alternate_number','alternate_no','alt_number','alt_no','alt_mobile','alternate_mobile','secondary_number','secondary_mobile','whatsapp_number','whatsapp'],
  // email
  email:                ['email','email_address','mail','e_mail','e-mail'],
  // website
  website:              ['website','web','url','website_url'],
  // city
  city:                 ['city','city_name','town','location'],
  // state
  state:                ['state','state_name','province'],
  // source
  source:               ['source','lead_source','referred_by'],
  // lead_status
  lead_status:          ['lead_status','status','lead_stage','stage'],
  // notes
  notes:                ['notes','note','remarks','comment','comments','description'],
};

// Build reverse lookup: raw_slug → canonical_key
const ALIAS_MAP = {};
for (const [canonical, aliases] of Object.entries(COLUMN_ALIASES)) {
  for (const alias of aliases) {
    ALIAS_MAP[alias] = canonical;
  }
}

function normalizeHeader(raw) {
  const slug = raw.toLowerCase().replace(/[\s\-./]+/g, '_').replace(/[^a-z0-9_]/g, '');
  return ALIAS_MAP[slug] || slug;
}

// ── CSV Parser ─────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const fields = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        fields.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }
    fields.push(cur.trim());
    return fields;
  };

  const rawHeaders = parseRow(lines[0]);
  const headers = rawHeaders.map((h) => normalizeHeader(h));

  const rows = lines.slice(1).map((line) => {
    const vals = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });

  return { headers, rows };
}

// ── Client-side row validation ─────────────────────────────────
const VALID_ORG_TYPES = ['college', 'university', 'company', 'training institute'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateRow(row, idx) {
  const errors = [];
  const label = `Row ${idx + 2}`;

  if (!row.organization_name || row.organization_name.trim() === '') {
    errors.push(`${label}: organization_name is required`);
  }

  if (row.mobile_number !== undefined) {
    const digits = (row.mobile_number || '').replace(/\D/g, '');
    if (digits.length !== 10) {
      errors.push(`${label}: mobile_number must be 10 digits (got "${row.mobile_number}")`);
    }
  }

  if (row.organization_type && row.organization_type.trim() !== '') {
    const t = row.organization_type.trim().toLowerCase();
    if (!VALID_ORG_TYPES.includes(t)) {
      errors.push(`${label}: organization_type "${row.organization_type}" must be one of College/University/Company/Training Institute`);
    }
  }

  if (row.email && row.email.trim() !== '') {
    if (!EMAIL_RE.test(row.email.trim())) {
      errors.push(`${label}: email "${row.email}" is invalid`);
    }
  }

  return errors;
}

// ── Status badge ───────────────────────────────────────────────
const HISTORY_STATUS = {
  completed:    { label: 'Completed',    cls: 'bg-green-100 text-green-800' },
  partial:      { label: 'Partial',      cls: 'bg-yellow-100 text-yellow-800' },
  failed:       { label: 'Failed',       cls: 'bg-red-100 text-red-800' },
  processing:   { label: 'Processing',   cls: 'bg-blue-100 text-blue-800' },
};

const HistoryBadge = ({ status }) => {
  const cfg = HISTORY_STATUS[status?.toLowerCase()] || { label: status || '—', cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// ── Progress Bar ───────────────────────────────────────────────
const ProgressBar = ({ value }) => (
  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
    <div
      className="h-3 rounded-full bg-[#843FEF] transition-all duration-300"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

// ── Main Component ─────────────────────────────────────────────
const CRMBulkUpload = () => {
  const [uiState, setUiState] = useState('idle'); // idle | file_selected | importing | done
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState({ headers: [], rows: [] });
  const [validationErrors, setValidationErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'success'|'error', msg }
  const fileInputRef = useRef(null);

  useEffect(() => { fetchHistory(); }, []);

  const showBanner = (type, msg) => {
    setBanner({ type, msg });
    setTimeout(() => setBanner(null), 5000);
  };

  // ── Fetch history ──
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/crm/bulk-upload/history`, { headers: authHeaders() });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || `Server error ${res.status}`);
      }
      const data = await res.json();
      setHistory(data.uploads || data.history || []);
    } catch (e) {
      console.error('History fetch error:', e);
      showBanner('error', 'Failed to fetch history: ' + e.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Download sample ──
  const downloadSample = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/crm/bulk-upload/sample`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crm_bulk_upload_sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showBanner('error', 'Failed to download sample CSV: ' + e.message);
    }
  };

  // ── Process selected file ──
  const processFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) {
      showBanner('error', 'Please select a valid .csv file.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      setParsedData({ headers, rows });
      const errs = rows.flatMap((row, i) => validateRow(row, i));
      setValidationErrors(errs);
      setUiState('file_selected');
    };
    reader.readAsText(f);
  }, []);

  // ── Drag & Drop ──
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  }, [processFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  // ── Import ──
  const handleImport = async () => {
    if (!file || parsedData.rows.length === 0) return;
    setUiState('importing');
    setImportProgress(20);

    try {
      setImportProgress(50);
      const res = await fetch(`${API_BASE}/api/crm/bulk-upload`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ filename: file.name, rows: parsedData.rows }),
      });
      setImportProgress(80);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || `Server error ${res.status}`);
      }
      const result = await res.json();
      setImportProgress(100);
      setImportResult(result);
      setUiState('done');
      showBanner('success', `Import complete: ${result.imported} imported, ${result.skipped} skipped, ${result.failed} failed.`);
      fetchHistory();
    } catch (e) {
      setUiState('file_selected');
      setImportProgress(0);
      showBanner('error', 'Import failed: ' + e.message);
    }
  };

  // ── Reset ──
  const handleReset = () => {
    setFile(null);
    setParsedData({ headers: [], rows: [] });
    setValidationErrors([]);
    setImportResult(null);
    setImportProgress(0);
    setUiState('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewRows = parsedData.rows.slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Banner */}
      {banner && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-semibold max-w-xl w-full ${banner.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {banner.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span className="flex-1">{banner.msg}</span>
          <button onClick={() => setBanner(null)}><X size={16} /></button>
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Upload size={26} className="text-[#843FEF]" />
              CRM Bulk Upload
            </h1>
            <p className="text-gray-500 text-sm mt-1">Import contacts from a CSV file into the CRM</p>
          </div>
          <button
            onClick={downloadSample}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:border-[#843FEF] hover:text-[#843FEF] transition-colors text-sm font-semibold shadow-sm"
          >
            <Download size={16} />
            Download Sample CSV
          </button>
        </div>

        {/* Upload Zone */}
        {(uiState === 'idle' || uiState === 'file_selected') && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-[#843FEF]" />
              Upload CSV File
            </h2>

            {uiState === 'idle' ? (
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all select-none ${
                  dragOver ? 'border-[#843FEF] bg-purple-50' : 'border-gray-300 hover:border-[#843FEF] hover:bg-purple-50/30'
                }`}
              >
                <Upload size={40} className={`mx-auto mb-3 ${dragOver ? 'text-[#843FEF]' : 'text-gray-400'}`} />
                <p className="text-gray-700 font-semibold text-base">Drag & drop your CSV here, or click to browse</p>
                <p className="text-gray-400 text-sm mt-1">Supports .csv files only</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => processFile(e.target.files[0])}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <FileText size={28} className="text-[#843FEF] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{file?.name}</p>
                  <p className="text-sm text-gray-500">{parsedData.rows.length} rows parsed</p>
                </div>
                <button
                  onClick={handleReset}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                  title="Remove file"
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Preview Table */}
        {uiState === 'file_selected' && parsedData.rows.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Eye size={20} className="text-[#843FEF]" />
                CSV Preview
                <span className="ml-2 text-sm font-normal text-gray-400">(showing first {previewRows.length} of {parsedData.rows.length} rows)</span>
              </h2>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                    {parsedData.headers.map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {previewRows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-gray-400 text-xs">{i + 2}</td>
                      {parsedData.headers.map((h) => (
                        <td key={h} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={row[h]}>{row[h] || <span className="text-gray-300 italic">—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Validation Summary */}
        {uiState === 'file_selected' && (
          <div className={`rounded-2xl shadow-sm border p-5 ${validationErrors.length === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-3 mb-2">
              {validationErrors.length === 0
                ? <CheckCircle size={20} className="text-green-600" />
                : <AlertCircle size={20} className="text-yellow-600" />
              }
              <h3 className="font-bold text-gray-800">
                {validationErrors.length === 0
                  ? 'Validation passed — all rows look good!'
                  : `${validationErrors.length} validation issue${validationErrors.length !== 1 ? 's' : ''} found`
                }
              </h3>
            </div>
            {validationErrors.length > 0 && (
              <ul className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {validationErrors.map((err, i) => (
                  <li key={i} className="text-sm text-yellow-800 flex items-start gap-2">
                    <span className="mt-0.5 flex-shrink-0">•</span>
                    {err}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleImport}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#843FEF] text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm shadow-sm disabled:opacity-50"
                disabled={parsedData.rows.length === 0}
              >
                <Upload size={16} />
                Import {parsedData.rows.length} Rows
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Importing progress */}
        {uiState === 'importing' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mx-auto">
              <RefreshCw size={26} className="text-[#843FEF] animate-spin" />
            </div>
            <p className="font-semibold text-gray-800 text-lg">Importing rows...</p>
            <div className="max-w-sm mx-auto">
              <ProgressBar value={importProgress} />
              <p className="text-sm text-gray-500 mt-1">{importProgress}%</p>
            </div>
          </div>
        )}

        {/* Import Result */}
        {uiState === 'done' && importResult && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <CheckCircle size={24} className="text-green-600" />
              <h2 className="text-lg font-bold text-gray-800">Import Complete</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Rows',  value: importResult.total,    color: 'text-gray-800',  bg: 'bg-gray-100'   },
                { label: 'Imported',    value: importResult.imported,  color: 'text-green-700', bg: 'bg-green-50'  },
                { label: 'Skipped',     value: importResult.skipped,   color: 'text-yellow-700',bg: 'bg-yellow-50' },
                { label: 'Failed',      value: importResult.failed,    color: 'text-red-700',   bg: 'bg-red-50'    },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center`}>
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value ?? 0}</p>
                  <p className="text-xs text-gray-500 font-medium mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm font-bold text-red-700 mb-2">Row-level errors:</p>
                <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <li key={i}>
                      • {typeof e === 'string' ? e : `Row ${e.row}: ${Array.isArray(e.errors) ? e.errors.join(', ') : JSON.stringify(e.errors)}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#843FEF] text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm"
            >
              <Upload size={16} />
              Upload Another File
            </button>
          </div>
        )}

        {/* Upload History */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Clock size={20} className="text-[#843FEF]" />
              Upload History
            </h2>
            <button
              onClick={fetchHistory}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#843FEF] transition-colors"
            >
              <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={22} className="text-[#843FEF] animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Upload size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No upload history yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['File Name', 'Uploaded By', 'Date', 'Total', 'Imported', 'Skipped', 'Failed', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-800 font-medium max-w-xs truncate" title={row.filename || row.file_name}>{row.filename || row.file_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{row.uploaded_by || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{row.total_rows ?? row.total ?? '—'}</td>
                      <td className="px-4 py-3 text-green-700 font-medium">{row.imported_rows ?? row.imported ?? '—'}</td>
                      <td className="px-4 py-3 text-yellow-700">{row.skipped_rows ?? row.skipped ?? '—'}</td>
                      <td className="px-4 py-3 text-red-700">{row.failed_rows ?? row.failed ?? '—'}</td>
                      <td className="px-4 py-3"><HistoryBadge status={row.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMBulkUpload;
